/**
 * @file homeLogic.ts
 * @brief The two Home rules that must match the phone exactly.
 *
 * @details
 * - `upcomingScheduled`: HorizontalScheduledSessions' "upcoming" filter.
 * - `mergeRecentNotes`: `FRONTEND/.../RecentNotes/recentNotesMerge.ts`.
 * Pure, with type-only imports, so `node --test` loads it directly.
 *
 * @author DES
 * @date 30-09-2026
 */

import type { VoiceNote } from "../notes/schemas";

export type HomeNote = VoiceNote & {
  is_owner?: boolean | null;
  shared_by_name?: string | null;
  shared_at?: string | null;
};

type ScheduledLike = {
  status?: string | null;
  time_status?: string | null;
  scheduled_at?: string | null;
};

const DONE = new Set(["completed", "attended", "cancelled", "canceled"]);

/** The phone's "upcoming" rule: not done, not missed, future or ≤90 min past. */
export function upcomingScheduled<T extends ScheduledLike>(rows: T[], now = Date.now()): T[] {
  return rows
    .filter((r) => !DONE.has(String(r.status ?? "").toLowerCase()))
    .filter((r) => r.time_status !== "missed")
    .filter((r) => {
      const at = r.scheduled_at ? Date.parse(r.scheduled_at) : NaN;
      return (
        r.time_status === "late" ||
        (Number.isFinite(at) && (at > now || now - at <= 90 * 60_000))
      );
    })
    .sort((a, b) => Date.parse(a.scheduled_at ?? "") - Date.parse(b.scheduled_at ?? ""))
    .slice(0, 10);
}

/**
 * How many items a Home rail fetches: as many cards as fit `railWidth`
 * (card 264 + gap 12), at least the phone's 5, at most 12. The web's rail is
 * capped at 1280px, so it stays 5; the Mac app fills the screen (DES
 * 19-10-2026: "bigger screen, show more cards").
 */
export function homeRowLimit(railWidth: number, cardStep = 276): number {
  if (!Number.isFinite(railWidth) || railWidth <= 0) return 5;
  return Math.min(12, Math.max(5, Math.ceil(railWidth / cardStep)));
}

/**
 * Own notes + at most 2 shared, newest first by when each became yours to see
 * (a shared note at its SHARE time), top `limit` (5, the phone's). The
 * phone's `mergeRecentNotes`.
 */
export function mergeRecentNotes(own: HomeNote[], shared: HomeNote[], limit = 5): HomeNote[] {
  const seen = new Set<string>();
  const at = (n: HomeNote) =>
    Date.parse((n.is_owner === false ? n.shared_at || n.created_at : n.created_at) ?? "") || 0;
  return [...own, ...shared.slice(0, 2)]
    .filter((n) => (seen.has(n.id) ? false : (seen.add(n.id), true)))
    .sort((a, b) => at(b) - at(a))
    .slice(0, limit);
}

